import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lightbulb } from 'lucide-react';
import { Modal } from '../ui/Modal';
import OsScroll from '../ui/OsScroll';
import { KNOWN_RULE_IDS, ruleKeys, formatScore, spamBand } from './spamRules';

/**
 * The reasons behind a spam score (owner, 08.09.2026: "on click on the spam
 * should open modal with table list with reasons / what should be
 * improved"; 09.09: "cards that would display better … modal slightly bigger
 * … on overflow use the scroll library"). One CARD per triggered rule: name +
 * points chip, the finding, the evidence line, and a "what to improve" box;
 * the list scrolls inside `OsScroll` (never the browser scrollbar) with a
 * viewport-bounded height. Copy comes from the locale bundles keyed
 * by rule id; an id this build does not know (a newer Rust) still renders —
 * the id itself, "Unknown rule", no hint — instead of throwing.
 *
 * Rendered inside `.mailc-view` (the app's `Modal` is not portaled), so the
 * `--mailc-*` variables apply. Only `mailc-*` and `rv-*` classes here (the
 * CSS hygiene guard in `mailCatcherView.test.js`).
 */
export default function SpamReasonsModal({ isOpen, onClose, spam }) {
  const { t, i18n } = useTranslation();
  const score = spam?.score ?? 0;
  const threshold = spam?.threshold ?? 5;
  const rules = Array.isArray(spam?.rules) ? spam.rules : [];
  // ONE threshold comparison in the codebase: the pill, the row badge and this
  // verdict all derive from `spamBand` (REVIEW.md N1 — exactly 5.0 is spam).
  const isSpam = spamBand(score, threshold) === 'high';
  const known = (id) => KNOWN_RULE_IDS.includes(id) && i18n.exists(ruleKeys(id).name);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('mailCatcher.spam.modalTitle')}
      subtitle={t('mailCatcher.spam.modalSubtitle', { score: formatScore(score), threshold: formatScore(threshold) })}
      size="3xl"
      contentClassName="mailc-spam-modal"
    >
      {rules.length === 0 ? (
        <div className="mailc-spam-empty" data-testid="mail-spam-empty">
          <div className="mailc-spam-empty-title">{t('mailCatcher.spam.noFindings')}</div>
          <div className="mailc-spam-empty-sub">{t('mailCatcher.spam.noFindingsHint', { count: KNOWN_RULE_IDS.length })}</div>
        </div>
      ) : (
        <OsScroll className="mailc-spam-scroll">
          <div className="mailc-spam-cards" data-testid="mail-spam-list">
            {rules.map((r, i) => {
              const id = String(r?.id ?? '');
              const keys = ruleKeys(id);
              const isKnown = known(id);
              const points = Number(r?.points) || 0;
              // Explicit class names (no template prefix) so the CSS hygiene scan sees each.
              const pointsClass =
                points >= 2 ? 'mailc-spam-points mailc-spam-points--high' : points >= 1 ? 'mailc-spam-points mailc-spam-points--mid' : 'mailc-spam-points mailc-spam-points--low';
              return (
                <div key={`${id}-${i}`} className="mailc-spam-card" data-testid="mail-spam-row" data-rule={id}>
                  <div className="mailc-spam-card-head">
                    <span className="mailc-spam-card-name">{isKnown ? t(keys.name) : id}</span>
                    <span className={pointsClass}>+{formatScore(points)}</span>
                  </div>
                  <div className="mailc-spam-card-desc">{isKnown ? t(keys.desc) : t('mailCatcher.spam.unknownRule', { id })}</div>
                  {r?.detail ? <div className="mailc-spam-detail">{String(r.detail)}</div> : null}
                  {isKnown && (
                    <div className="mailc-spam-card-hint">
                      <Lightbulb size={13} aria-hidden="true" />
                      <span className="mailc-spam-card-hint-label">{t('mailCatcher.spam.colHint')}</span>
                      <span className="mailc-spam-card-hint-text">{t(keys.hint)}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </OsScroll>
      )}
      <div className="mailc-spam-total" data-testid="mail-spam-total">
        <span className="mailc-spam-total-label">{t('mailCatcher.spam.total')}</span>
        <span className="mailc-spam-total-score">{formatScore(score)}</span>
        <span className={`mailc-spam-verdict${isSpam ? ' mailc-spam-verdict--spam' : ''}`} data-testid="mail-spam-verdict">
          {isSpam ? t('mailCatcher.spam.verdictSpam') : t('mailCatcher.spam.verdictOk')}
        </span>
      </div>
      <p className="mailc-spam-note">{t('mailCatcher.spam.localOnly')}</p>
    </Modal>
  );
}
